import React, { useState } from 'react';
import { DollarSign, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

/**
 * ConsultationFeeDisplay - Shows consultation fee assigned vs paid with visual indicators
 */
export function ConsultationFeeDisplay({ 
  assignedFee, 
  paidFee, 
  status,
  onEditPaidFee,
  isLoading = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  // Default paidFee to assignedFee if not set or 0
  const paidFeeDefault = (paidFee === undefined || paidFee === null || paidFee === '' || isNaN(Number(paidFee)) || Number(paidFee) === 0)
    ? assignedFee
    : paidFee;
  const [editedAmount, setEditedAmount] = useState(paidFeeDefault || '0');

  const assigned = parseFloat(assignedFee || 0);
  const paid = parseFloat(paidFeeDefault || 0);
  const remaining = assigned - paid;
  const isPaid = paid >= assigned;
  const isPartialPaid = paid > 0 && paid < assigned;

  const handleSaveEdit = () => {
    const newAmount = parseFloat(editedAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      alert('Invalid amount');
      return;
    }
    onEditPaidFee(newAmount);
    setIsEditing(false);
  };

  // If paidFee prop changes, update editedAmount (but NOT while editing)
  React.useEffect(() => {
    if (!isEditing) {
      setEditedAmount(paidFeeDefault || '0');
    }
  }, [paidFee, assignedFee, isEditing]);

  return (
    <div style={{
      background: 'var(--surface)',
      borderRadius: 'var(--r-lg)',
      padding: '20px',
      border: '1.5px solid var(--border)',
      marginBottom: '20px',
    }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <DollarSign size={16} /> Consultation Fee
      </h4>

      {/* Fee Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {/* Assigned Fee */}
        <div style={{
          background: 'var(--surface-2)',
          padding: '12px',
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--border)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginBottom: 6 }}>
            Assigned
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>
            ₹{assigned.toFixed(2)}
          </div>
        </div>

        {/* Paid Fee */}
        <div style={{
          background: isPartialPaid ? 'var(--amber-lt)' : isPaid ? 'var(--emerald-lt)' : 'var(--rose-lt)',
          padding: '12px',
          borderRadius: 'var(--r-md)',
          border: `1.5px solid ${isPartialPaid ? 'var(--amber)' : isPaid ? 'var(--emerald)' : 'var(--rose)'}`,
          textAlign: 'center',
          cursor: isEditing ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600, marginBottom: 6 }}>
            Paid {!isEditing && '(Click to edit)'}
          </div>
          {isEditing ? (
            <input
              type="number"
              value={editedAmount}
              onChange={(e) => setEditedAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: 14,
                fontWeight: 700,
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                fontFamily: 'var(--mono)',
                textAlign: 'center',
              }}
              autoFocus
            />
          ) : (
            <div 
              style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}
              onClick={() => setIsEditing(true)}
            >
              ₹{paid.toFixed(2)}
            </div>
          )}
        </div>

        {/* Remaining */}
        <div style={{
          background: 'var(--surface-2)',
          padding: '12px',
          borderRadius: 'var(--r-md)',
          border: '1px solid var(--border)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginBottom: 6 }}>
            Remaining
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: remaining > 0 ? 'var(--rose)' : 'var(--emerald)' }}>
            ₹{Math.max(0, remaining).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          height: '6px',
          borderRadius: '3px',
          background: 'var(--surface-3)',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, (paid / assigned) * 100)}%`,
            background: isPaid ? 'var(--emerald)' : isPartialPaid ? 'var(--amber)' : 'var(--rose)',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, textAlign: 'right' }}>
          {((paid / assigned) * 100).toFixed(0)}% Paid
        </div>
      </div>

      {/* Status Badge */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: 'var(--r-md)',
            fontSize: 12,
            fontWeight: 600,
            background: isPaid ? 'var(--emerald-lt)' : isPartialPaid ? 'var(--amber-lt)' : 
                        status === 'PAYMENT_PENDING' ? 'var(--rose-lt)' : 'var(--surface-2)',
            color: isPaid ? 'var(--emerald)' : isPartialPaid ? 'var(--amber)' : 
                   status === 'PAYMENT_PENDING' ? 'var(--rose)' : 'var(--text-2)',
            border: `1px solid ${isPaid ? 'var(--emerald)' : isPartialPaid ? 'var(--amber)' : 
                                status === 'PAYMENT_PENDING' ? 'var(--rose)' : 'var(--border)'}`,
          }}>
            {isPaid ? <CheckCircle2 size={14} /> : isPartialPaid ? <AlertTriangle size={14} /> : 
             status === 'PAYMENT_PENDING' ? <Clock size={14} /> : null}
            <span>
              {isPaid ? 'Payment Complete' : isPartialPaid ? 'Partial Payment' : 
               status === 'PAYMENT_PENDING' ? 'Awaiting Payment' : status}
            </span>
          </div>
      </div>

      {/* Edit Buttons */}
      {isEditing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={() => {
              setEditedAmount(paid);
              setIsEditing(false);
            }}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--border)',
              background: 'var(--surface-2)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 'var(--r-md)',
              border: 'none',
              background: 'var(--teal)',
              color: 'white',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}
